import { DEFAULT_VARIANT_VALUE, EMPTY_DIMENSIONS_JSON } from './constants';

export type VariantDimensions = Record<string, string>;

export function parseDimensions(raw: unknown): VariantDimensions {
  if (typeof raw !== 'string' || raw.length === 0) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    const dimensions: VariantDimensions = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'string' && value.length > 0) {
        dimensions[key] = value;
      }
    }

    return dimensions;
  } catch {
    return {};
  }
}

export function stringifyDimensions(dimensions: VariantDimensions): string {
  const entries = Object.entries(dimensions).filter(([, value]) => typeof value === 'string' && value.length > 0);
  if (entries.length === 0) {
    return EMPTY_DIMENSIONS_JSON;
  }

  // Stable key order for cleaner diffs / collab.
  entries.sort(([a], [b]) => a.localeCompare(b));

  return JSON.stringify(Object.fromEntries(entries));
}

function isDefaultVariant(dimensions: VariantDimensions): boolean {
  return Object.keys(dimensions).length === 0;
}

/** Channel select value: `'default'` when no channelId key, else the channel id. */
export function getChannelId(dimensions: VariantDimensions): string {
  return dimensions.channelId || DEFAULT_VARIANT_VALUE;
}

/**
 * MVP channel picker: Default → `{}`; channel → `{ channelId }` only
 * (clears other dimension keys so the select stays channel-centric).
 */
export function setChannelId(channelId: string): VariantDimensions {
  if (!channelId || channelId === DEFAULT_VARIANT_VALUE) {
    return {};
  }

  return { channelId };
}

type DirectiveAttributes = Record<string, string | null | undefined> | null | undefined;

/** Map remark-directive attributes → dimensions JSON (drops boolean `default` key). */
export function dimensionsFromDirectiveAttributes(attributes: DirectiveAttributes): string {
  const dimensions: VariantDimensions = {};
  for (const [key, value] of Object.entries(attributes ?? {})) {
    if (key === 'default') {
      continue;
    }
    if (typeof value === 'string' && value.length > 0) {
      dimensions[key] = value;
    }
  }

  return stringifyDimensions(dimensions);
}

/** Map dimensions JSON → remark-directive attributes (`{default}` or key=value). */
export function directiveAttributesFromDimensions(raw: unknown): Record<string, string> {
  const dimensions = parseDimensions(raw);
  if (isDefaultVariant(dimensions)) {
    return { default: '' };
  }

  return { ...dimensions };
}
